import React from 'react';
import { useTranslation } from 'react-i18next';
import { format as formatDate } from 'date-fns';
import { groupBy, meanBy } from 'lodash';

import SpotCheckChart from '../../charts/SpotCheckChart';
import ReportCard from 'components/ReportsModule/components/common/ReportCard';
import { KeyStudentEngagementSpotCheckResult } from 'types/schema-generated-types';

const StudentSpotCheckChart: React.FC<{
  reportData?: KeyStudentEngagementSpotCheckResult[];
  dateIntervals: string[];
  dateIntervalFormat: string;
  loading: boolean;
}> = ({ reportData = [], dateIntervals, dateIntervalFormat, loading }) => {
  const { t } = useTranslation(['Reports', 'ReportsHelpCharts']);

  const intervalValues = reportData.reduce(
    (acc: { date: string; wpm: number; accuracy: number }[], { dateCompleted, wpm, accuracy }) => {
      if (!dateCompleted) return acc;
      return [
        ...acc,
        { date: formatDate(new Date(dateCompleted), dateIntervalFormat), wpm, accuracy }
      ];
    },
    []
  );

  const intervalValuesByDate = groupBy(intervalValues, 'date');
  const { wpmSeries, accuracySeries } = dateIntervals.reduce(
    (acc, date) => {
      const dateIntervalValues = intervalValuesByDate[date];
      if (!dateIntervalValues) {
        acc.wpmSeries.push(0);
        acc.accuracySeries.push(0);
      } else {
        acc.wpmSeries.push(meanBy(dateIntervalValues, 'wpm'));
        acc.accuracySeries.push(meanBy(dateIntervalValues, 'accuracy'));
      }

      return acc;
    },
    {
      wpmSeries: [] as number[],
      accuracySeries: [] as number[]
    }
  );

  return (
    <ReportCard
      title={t('Spot Checks')}
      helpText={t('spotChecks', { ns: 'ReportsHelpCharts' })}
      loading={loading}
      reportData={reportData}
    >
      <SpotCheckChart
        wpmSeries={wpmSeries}
        accuracySeries={accuracySeries}
        labels={dateIntervals}
      />
    </ReportCard>
  );
};

export default StudentSpotCheckChart;
